/* eslint-disable */
import {useForm} from "@mantine/form";
import {Device, DeviceApi} from "../../api/device/DeviceApi";
import {useEffect, useState} from "react";
import {Button, Flex, Modal, NumberInput, Select, Stack, Textarea, TextInput} from "@mantine/core";
import {showNotification} from "@mantine/notifications";
import {Company, CompanyAPI} from "../../api/company/CompanyAPI";
import {useLoading} from "../../hooks/useLoading";

export type DeviceEditDialogAttrs = {
    device?: Device;

    isOpen: boolean;
    onUpdated: () => void;
    onClosedWithoutUpdate: () => void;
}

type EditDeviceType = {
    unit: string,
    name: string,
    description?: string,
    location?: string,
    generator?: string,
    ftpDirectory?: string,
    thermostat?: string,
    points?: string,
    clientId?: string,
    minAllowableTemp?: number,
    maxAllowableTemp?: number,
    serialNumber?: string;
    clientEmailAddress?: string,
    clientVisibleName?: string;
    clientPhoneNumber?: string;
    payment?: string | null;
    medicalTransport?: string | null;
}

function DeviceEditDialog(attrs: DeviceEditDialogAttrs) {

    const [companyList, setCompanyList] = useState<Company[]>();
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [loading, startLoading, stopLoading] = useLoading();

    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            clientPhoneNumber: undefined,
            clientVisibleName: undefined,
            clientEmailAddress: undefined,
            unit: '',
            name: '',
            description: undefined,
            location: undefined,
            generator: undefined,
            ftpDirectory: undefined,
            thermostat: undefined,
            points: undefined,
            minAllowableTemp: undefined,
            maxAllowableTemp: undefined,
            serialNumber: undefined,
            payment: undefined,
            medicalTransport: undefined,
        } as EditDeviceType,

        validate: {
            unit: (e) => e.length > 2 ? null : 'Przynajmniej dwa znaki',
            name: (e) => e.length > 2 ? null : 'Przynajmniej dwa znaki'
        }
    });

    const loadData = () => {
        form.setValues({
            unit: attrs.device?.unit ?? '',
            name: attrs.device?.name ?? '',
            description: attrs.device?.description ?? '',
            location: attrs.device?.location ?? '',
            generator: attrs.device?.generator ?? '',
            ftpDirectory: attrs.device?.ftpDirectory ?? '',
            thermostat: attrs.device?.thermostat ?? '',
            points: attrs.device?.points ?? '',
            minAllowableTemp: attrs.device?.minAllowableTemp,
            maxAllowableTemp: attrs.device?.maxAllowableTemp,
            clientVisibleName: attrs.device?.clientVisibleName ?? '',
            clientPhoneNumber: attrs.device?.clientPhoneNumber ?? '',
            clientEmailAddress: attrs.device?.clientEmailAddress ?? '',
            payment: attrs.device?.payment ?? '',
            serialNumber: attrs.device?.serialNumber ?? "",
            medicalTransport: attrs.device?.medicalTransport ?? '',
        });
        setCompanyId(attrs.device?.company?.id ?? null);
    }

    const doSubmitError = () => {
        showNotification({
            title: 'Nie udało się zapisać zmian',
            message: 'Jeśli problem będzie się utrzymywał, proszę skontaktować się z administratorem',
            color: 'red'
        });

        form.setFieldError('unit', 'Nie udało się zapisać zmian.');
    }

    const doSubmitSuccess = () => {
        showNotification({
            title: 'Zapisano!',
            message: 'Zmiany zostały zapisane',
            color: 'green'
        });
    }

    const doSubmit = (values: EditDeviceType) => {
        startLoading();

        const editPayload = {
            id: attrs.device?.id ?? '',
            clientId: attrs.device?.client?.id,
            companyId: companyId ?? null,
            ...values
        }

        DeviceApi.edit(editPayload)
            .then(doSubmitSuccess)
            .then(attrs.onUpdated)
            .catch(doSubmitError)
            .finally(stopLoading);
    }

    useEffect(loadData, [attrs.device]);

    useEffect(() => {
      if (attrs.isOpen) {
        CompanyAPI.listAsync().then(setCompanyList);
      }
    }, [attrs.isOpen]);

    return (
        <Modal opened={attrs.isOpen} onClose={attrs.onClosedWithoutUpdate} title="Edycja urządzenia">
            <form onSubmit={form.onSubmit(doSubmit)}>
                <Stack>
                    <TextInput
                        withAsterisk
                        label="Jednostka"
                        placeholder="Odpowiednik <UNIT> z raportów"
                        variant="filled"
                        key={form.key('unit')}
                        { ...form.getInputProps('unit') }
                    />

                    <TextInput
                        withAsterisk
                        label="Nazwa urządzenia"
                        placeholder="Nazwa urządzenia"
                        variant="filled"
                        key={form.key('name')}
                        { ...form.getInputProps('name') }
                    />

                    <Textarea
                        label="Opis"
                        placeholder="Dowolny opis urządzenia"
                        variant="filled"
                        minRows={3}
                        resize={'vertical'}
                        key={form.key('description')}
                        { ...form.getInputProps('description') }
                    />

                    <TextInput
                        label="Nazwa klienta"
                        placeholder="Nazwa klienta"
                        variant="filled"
                        key={form.key('clientVisibleName')}
                        { ...form.getInputProps('clientVisibleName') }
                    />

                    <TextInput
                        label="E-mail klienta"
                        placeholder="E-mail klienta"
                        variant="filled"
                        key={form.key('clientEmailAddress')}
                        { ...form.getInputProps('clientEmailAddress') }
                    />

                    <TextInput
                        label="Numer telefonu"
                        placeholder="Numer telefonu"
                        variant="filled"
                        key={form.key('clientPhoneNumber')}
                        { ...form.getInputProps('clientPhoneNumber') }
                    />

                    <TextInput
                        label="Folder FTP"
                        placeholder="Gdzie będą zapisywane wygenerowane raporty"
                        variant="filled"
                        key={form.key('ftpDirectory')}
                        { ...form.getInputProps('ftpDirectory') }
                    />

                    <TextInput
                        label="Termostat"
                        placeholder=""
                        variant="filled"
                        key={form.key('thermostat')}
                        { ...form.getInputProps('thermostat') }
                    />

                    <TextInput
                        label="Generator"
                        placeholder=""
                        variant="filled"
                        key={form.key('generator')}
                        { ...form.getInputProps('generator') }
                    />

                    <TextInput
                        label="Płatność"
                        placeholder="Czy klient płaci za urządzenie?"
                        variant="filled"
                        key={form.key('payment')}
                        { ...form.getInputProps('payment') }
                    />

                    <Textarea
                        label="Uwagi"
                        placeholder="Uwagi do tego urządzenia"
                        variant="filled"
                        minRows={3}
                        resize={'vertical'}
                        key={form.key('points')}
                        { ...form.getInputProps('points') }
                    />

                    <Flex gap="md">
                        <NumberInput
                            step={0.1}
                            label={"Min."}
                            key={form.key('minAllowableTemp')}
                            { ...form.getInputProps('minAllowableTemp') }
                        />

                        <NumberInput
                            step={0.1}
                            label={"Maks."}
                            key={form.key('maxAllowableTemp')}
                            { ...form.getInputProps('maxAllowableTemp') }
                        />
                    </Flex>

                    <TextInput
                        label="Numer seryjny"
                        placeholder=""
                        variant="filled"
                        key={form.key('serialNumber')}
                        { ...form.getInputProps('serialNumber') }
                    />

                    <TextInput
                        label="Transport medyczny"
                        placeholder=""
                        variant="filled"
                        key={form.key('medicalTransport')}
                        { ...form.getInputProps('medicalTransport') }
                    />

                  <Select
                    searchable
                    clearable
                    label="Firma"
                    value={companyId}
                    placeholder="Wybierz firmę lub pozostaw puste"
                    onChange={(t) => setCompanyId(t)}
                    data={companyList?.map(c => ({ label: c.title, value: c.id })) ?? []}
                  />

                    <Button type='submit' loading={loading}>
                        Zapisz
                    </Button>
                </Stack>
            </form>
        </Modal>
    );
}

export default DeviceEditDialog;
